import queryKeys from '@/constants/queryKeys';
import { useDeleteTemplateMutation } from '@/hooks/api/template-gallery/useDeleteTemplateMutation';
import { showToast } from '@/utils/monday';
import { Text } from '@vibe/core';
import {
  Modal,
  ModalBasicLayout,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@vibe/core/next';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

interface DeleteTemplateModalProps {
  show: boolean;
  onClose: () => void;
  templateId: string;
  isInUse: boolean;
}

const DeleteTemplateModal = ({
  show,
  onClose,
  templateId,
  isInUse,
}: DeleteTemplateModalProps) => {
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);

  const { mutateAsync: deleteTemplate } = useDeleteTemplateMutation(templateId);
  const queryClient = useQueryClient();

  const handleDeleteTemplate = async ({ force = false }: { force?: boolean }) => {
    setIsDeletingTemplate(true);
    try {
      await deleteTemplate({ force });
      await queryClient.invalidateQueries(
        queryKeys.TEMPLATE_GALLERY_USER_TEMPLATES,
      );
      await queryClient.invalidateQueries(queryKeys.GET_FILES_ITEM);
      setIsDeletingTemplate(false);
      showToast({
        message: 'Template has been deleted successfully',
        type: 'success',
      });
      onClose();
    } catch (error) {
      showToast({
        // @ts-expect-error - TODO: fix this
        message: error?.response?.data?.message || 'Failed to delete template',
        type: 'error',
      });
      console.error(error);
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const renderLayout = () => {
    if (isInUse) {
      return (
        <>
          <ModalBasicLayout>
            <ModalHeader
              title="Confirm Deletion"
              description="This template is being used in other boards."
            />
            <ModalContent>
              <Text type="text1" ellipsis={false}>
                Are you sure you want to delete this template? <br />
                This action is <strong>irreversible</strong>.
              </Text>
            </ModalContent>
          </ModalBasicLayout>
          <ModalFooter
            primaryButton={{
              onClick: () => handleDeleteTemplate({ force: true }),
              loading: isDeletingTemplate,
              text: 'Delete Anyway',
            }}
            secondaryButton={{
              onClick: onClose,
              text: 'Cancel',
            }}
          />
        </>
      );
    }
    return (
      <>
        <ModalBasicLayout>
          <ModalHeader
            title="Confirm deletion"
          />
          <ModalContent>
            <Text type="text1" ellipsis={false}>
              Are you sure you want to delete this template? <br />
              This action is <strong>irreversible</strong>.
            </Text>
          </ModalContent>
        </ModalBasicLayout>
        <ModalFooter
          primaryButton={{
            onClick: () => handleDeleteTemplate({ force: false }),
            loading: isDeletingTemplate,
            text: 'Confirm',
          }}
          secondaryButton={{
            onClick: onClose,
            text: 'Cancel',
          }}
        />
      </>
    );
  };

  return (
    <Modal
      show={show}
      id="template-delete-confirmation-dialog"
      onClose={onClose}
      size="small"
      alertModal
    >
      {renderLayout()}
    </Modal>
  );
};

export default DeleteTemplateModal;
